import { tokenService } from "../services/tokenService";
import React, { useState, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sprout, Mail, Lock, UserPlus, Shield, Eye, EyeOff, ShieldCheck, KeyRound } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const { login, verifyOtpB2B, resendOtpB2B } = useAuth();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // État OTP B2B
  const [otpState, setOtpState] = useState(null); // { challengeId, emailHint }
  const [otpCode, setOtpCode] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Helper function to get redirect path based on user type
  const getRedirectPath = (userData) => {
    // Partenaire B2B avec réseau de coopératives (RSE/PSE Network Manager) → dashboard réseau
    if (userData?.user_type === 'acheteur' && userData?.network_manager) {
      return '/exporter/dashboard';
    }
    // Partenaire B2B (acheteur avec flag b2b_partner) → dashboard partenaire dédié
    if (userData?.user_type === 'acheteur' && userData?.b2b_partner) {
      return '/partner/dashboard';
    }
    switch (userData?.user_type) {
      case 'admin':
        return '/admin/dashboard';
      case 'cooperative':
        return '/cooperative/dashboard';
      case 'carbon_auditor':
        return '/auditor/dashboard';
      case 'auditor':
        return '/auditor/read-only';
      case 'exporter':
        return '/exporter/dashboard';
      case 'field_agent':
      case 'agent_terrain':
        return '/agent/terrain';
      case 'producteur':
      case 'producer':
      case 'farmer':
        return '/farmer/dashboard';
      case 'acheteur':
        return '/buyer/dashboard';
      case 'fournisseur':
        return '/supplier/dashboard';
      default:
        return '/profile';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.identifier, formData.password);

    setLoading(false);

    // OTP 2FA requis pour les Partenaires B2B ET les Coopératives (opt-in)
    if (result.requiresOtp) {
      setOtpState({
        challengeId: result.otpChallengeId,
        emailHint: result.emailHint,
        audience: result.audience || 'Partenaire B2B',
        userType: result.otpUserType || 'b2b_partner',
      });
      toast({
        title: 'Vérification 2FA requise',
        description: `Code envoyé à ${result.emailHint}`,
      });
      return;
    }

    if (result.success) {
      toast({
        title: 'Connexion réussie!',
        description: 'Bienvenue sur GreenLink'
      });
      
      // Use the user data from the result to determine redirect
      if (result.user) {
        const redirectPath = redirectParam || getRedirectPath(result.user);
        navigate(redirectPath);
      } else {
        const token = tokenService.getToken();
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const redirectPath = redirectParam || getRedirectPath({ user_type: payload.user_type });
            navigate(redirectPath);
          } catch {
            navigate(redirectParam || '/profile');
          }
        } else {
          navigate(redirectParam || '/profile');
        }
      }
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive'
      });
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast({ title: 'Code invalide', description: 'Le code fait 6 chiffres.', variant: 'destructive' });
      return;
    }
    setVerifyingOtp(true);
    const result = await verifyOtpB2B(otpState.challengeId, otpCode);
    setVerifyingOtp(false);
    if (result.success) {
      toast({ title: 'Vérification réussie', description: 'Bienvenue !' });
      navigate(getRedirectPath(result.user));
    } else {
      toast({ title: 'Code incorrect', description: result.error, variant: 'destructive' });
      setOtpCode('');
    }
  };

  const handleResendOtp = async () => {
    const result = await resendOtpB2B(otpState.challengeId);
    if (result.success) {
      setOtpState({ challengeId: result.otpChallengeId, emailHint: result.emailHint });
      setOtpCode('');
      toast({ title: 'Nouveau code envoyé', description: `Vérifiez ${result.emailHint}` });
    } else {
      toast({ title: 'Erreur', description: result.error, variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2d5a4d] via-[#235043] to-[#1a4038] flex items-center justify-center p-4 sm:p-6">
      <Card className="w-full max-w-md p-6 sm:p-8">

        {/* ===== ÉCRAN OTP 2FA (Partenaires B2B + Coopératives) ===== */}
        {otpState ? (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-lg bg-[#1A3622] flex items-center justify-center shrink-0">
                <ShieldCheck className="w-7 h-7 text-[#d4a574]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Vérification 2FA</h1>
                <p className="text-sm text-gray-600" data-testid="otp-audience-subtitle">
                  Accès {otpState.audience || 'Partenaire B2B'} sécurisé
                </p>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-sm text-emerald-900">
              <KeyRound className="inline h-4 w-4 mr-1.5" />
              Un code à 6 chiffres a été envoyé à <strong>{otpState.emailHint}</strong>. Il est valable <strong>10 minutes</strong>.
            </div>

            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div>
                <Label htmlFor="otpCode">Code de vérification</Label>
                <Input
                  id="otpCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest font-mono"
                  required
                  autoFocus
                  data-testid="otp-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1A3622] hover:bg-[#235043] text-white py-6 text-base font-bold"
                disabled={verifyingOtp || otpCode.length !== 6}
                data-testid="otp-verify-btn"
              >
                {verifyingOtp ? 'Vérification...' : 'Valider et accéder au dashboard'}
              </Button>
            </form>

            <div className="mt-5 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={handleResendOtp}
                className="text-[#1A3622] hover:underline"
                data-testid="otp-resend-btn"
              >
                Renvoyer le code
              </button>
              <button
                type="button"
                onClick={() => { setOtpState(null); setOtpCode(''); }}
                className="text-gray-500 hover:underline"
                data-testid="otp-cancel-btn"
              >
                ← Retour à la connexion
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <img
                src="/icon-192.png"
                alt="GreenLink Agritech"
                className="w-14 h-14 rounded-lg shadow-md object-cover ring-1 ring-black/5"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Se connecter</h1>
                <p className="text-sm text-gray-600">Accédez à votre compte</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email or Phone */}
          <div>
            <Label htmlFor="identifier">Email ou Téléphone</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <Input
                id="identifier"
                type="text"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="exemple@email.com ou +225 0707070707"
                value={formData.identifier}
                onChange={(e) => updateField('identifier', e.target.value)}
                className="pl-10"
                required
                data-testid="identifier-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <Label htmlFor="password">Mot de passe</Label>
              <Link to="/forgot-password" className="text-xs text-[#2d5a4d] hover:underline">
                Mot de passe oublié?
              </Link>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="Votre mot de passe"
                value={formData.password}
                onChange={(e) => updateField('password', e.target.value)}
                className="pl-10 pr-10"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-[#2d5a4d] hover:bg-[#1a4038] text-white py-6 text-lg"
            disabled={loading}
          >
            {loading ? 'Connexion en cours...' : 'Se connecter'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Pas encore de compte?{' '}
            <Link to="/register" className="text-[#2d5a4d] font-semibold hover:underline">
              S'inscrire
            </Link>
          </p>
        </div>

        {/* Activation Buttons */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center mb-3">
            Inscrit par votre coopérative ? Activez votre accès web
          </p>
          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start border-green-200 hover:bg-green-50 text-green-700"
              onClick={() => navigate('/activate-member')}
              data-testid="activate-member-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Je suis planteur — Activer mon accès web
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start border-cyan-200 hover:bg-cyan-50 text-cyan-700"
              onClick={() => navigate('/activate-agent')}
              data-testid="activate-agent-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              Je suis agent terrain — Activer mon accès web
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start border-[#1A3622]/30 hover:bg-[#1A3622]/5 text-[#1A3622]"
              onClick={() => navigate('/register-b2b')}
              data-testid="partner-b2b-register-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              Exportateur / Auditeur / Partenaire B2B — Demander un accès
            </Button>
          </div>
        </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default Login;